/**
 * Card de Inbox para o painel de Status do WhatsApp
 * 
 * Exibe uma inbox com indicador visual de status ativos
 * e permite acesso às ações de status
 * 
 * @version 1.0.0
 * @release 2025-02-08
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MessageSquare, Plus, ChevronRight } from 'lucide-react';
import { WhatsAppStatusIconSimple } from '@/components/icons/WhatsAppStatusIcon';
import type { InboxData } from '@/hooks/useInboxes';

interface StatusInboxCardProps {
  /** Dados da inbox */
  inbox: InboxData;
  /** Contagem de status ativos (published + scheduled) */
  activeStatusCount: number;
  /** Se esta inbox está selecionada */
  isSelected?: boolean;
  /** Callback ao clicar na inbox */
  onClick: () => void;
  /** Callback ao clicar em criar novo status */
  onCreateStatus: () => void;
}

/**
 * Card que representa uma inbox no painel de Status
 * Mostra indicador visual se há status ativos
 */
export function StatusInboxCard({
  inbox,
  activeStatusCount,
  isSelected,
  onClick,
  onCreateStatus,
}: StatusInboxCardProps) {
  // Verifica se tem status ativo
  const hasActiveStatus = activeStatusCount > 0;

  // Nome do provider para exibição
  const providerName = inbox.provider?.display_name || inbox.provider?.name || 'WhatsApp';

  return (
    <div
      className={cn(
        'group flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
        'hover:bg-accent/50 hover:border-accent',
        isSelected && 'bg-accent border-accent',
        !hasActiveStatus && 'opacity-80'
      )}
      onClick={onClick}
    >
      {/* Indicador de Status */}
      <div className="relative flex-shrink-0">
        {/* Círculo com indicador visual */}
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            hasActiveStatus
              ? 'bg-primary/10 border-2 border-primary'
              : 'bg-muted border-2 border-dashed border-muted-foreground/40'
          )}
        >
          {hasActiveStatus ? (
            <WhatsAppStatusIconSimple 
              size={18} 
              className="text-primary" 
            />
          ) : (
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          )}
        </div>

        {/* Badge de contagem */}
        {hasActiveStatus && (
          <Badge
            variant="default"
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5 text-[10px] font-semibold"
          >
            {activeStatusCount}
          </Badge>
        )}
      </div>

      {/* Informações da Inbox */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">
          {inbox.name}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {providerName}
          {hasActiveStatus && (
            <span className="ml-1.5">
              • {activeStatusCount} {activeStatusCount === 1 ? 'status ativo' : 'status ativos'}
            </span>
          )}
          {!hasActiveStatus && (
            <span className="ml-1.5">• Sem status ativo</span>
          )}
        </p>
      </div>

      {/* Ações */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={(e) => {
            e.stopPropagation();
            onCreateStatus();
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}
